import { useEffect, useMemo, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { api } from "@/lib/api";
import { MapPin, ArrowLeft } from "@phosphor-icons/react";

const W = 960;
const H = 420;

// d3-geo spherical rendering expects exterior rings clockwise (opposite of RFC 7946);
// some GeoJSON files are CCW and render as an inverted "tiny dot" world. Fix winding here.
function rewindGeo(geo) {
  const shoelace = (ring) => {
    let a = 0;
    for (let i = 0; i < ring.length - 1; i++) a += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
    return a / 2;
  };
  const fix = (poly) =>
    poly.map((ring, i) => {
      const isCCW = shoelace(ring) > 0;
      const wantCCW = i !== 0;
      return isCCW === wantCCW ? ring : [...ring].reverse();
    });
  geo.features.forEach((f) => {
    if (f.geometry.type === "Polygon") f.geometry.coordinates = fix(f.geometry.coordinates);
    else if (f.geometry.type === "MultiPolygon") f.geometry.coordinates = f.geometry.coordinates.map(fix);
  });
  return geo;
}

export default function GeoMap({ events = [] }) {
  const [eventId, setEventId] = useState("");
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState({ total: 0, provinces: [] });
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    fetch("/indonesia-provinces.geojson").then((r) => r.json()).then((g) => setGeo(rewindGeo(g))).catch(() => {});
  }, []);

  useEffect(() => {
    const q = eventId ? `?event_id=${eventId}` : "";
    api.get(`/stats/geo${q}`).then((r) => { setData(r.data || {}); setSelected(null); }).catch(() => {});
  }, [eventId]);

  const provinces = data.provinces || [];
  const counts = useMemo(() => Object.fromEntries(provinces.map((p) => [p.province, p.count])), [provinces]);
  const maxCount = Math.max(1, ...provinces.map((p) => p.count));

  const paths = useMemo(() => {
    if (!geo) return [];
    const proj = geoMercator().fitExtent([[8, 8], [W - 8, H - 8]], geo);
    const gen = geoPath(proj);
    return geo.features.map((f) => ({ name: f.properties.Propinsi, d: gen(f) })).filter((p) => p.d);
  }, [geo]);

  const fillFor = (name) => {
    const c = counts[name] || 0;
    if (!c) return "#18181B";
    const t = 0.25 + 0.75 * (c / maxCount);
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    return `rgb(${lerp(80, 255)},${lerp(18, 59)},${lerp(22, 48)})`;
  };

  const sel = selected ? provinces.find((p) => p.province === selected) : null;
  const hoverInfo = hover ? `${hover} — ${counts[hover] || 0} peserta` : null;

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 border-t border-white/10" data-testid="geo-section">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">PETA SEBARAN</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3">DARI SABANG SAMPAI MERAUKE</h2>
          <p className="mt-2 text-sm text-white/50 max-w-xl">
            Sebaran kota tujuan pengiriman peserta di seluruh Indonesia. Klik provinsi untuk melihat rincian kotanya.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-num text-3xl text-[#FF3B30]" data-testid="geo-total">{data.total || 0}</span>
          <span className="text-[10px] tracking-widest text-white/50 font-display">TOTAL<br />PENDAFTAR</span>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="tactical-input max-w-[220px]" data-testid="geo-event-filter">
            <option value="">Semua Event</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat p-4 md:p-6">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="geo-map">
            {paths.map((p) => {
              const active = (counts[p.name] || 0) > 0;
              return (
                <path
                  key={p.name}
                  d={p.d}
                  fill={fillFor(p.name)}
                  stroke={hover === p.name ? "#FFFFFF" : "#0A0A0A"}
                  strokeWidth={hover === p.name ? 1.5 : 0.6}
                  className={active ? "cursor-pointer transition-[fill] duration-300" : ""}
                  onMouseEnter={() => setHover(p.name)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => active && setSelected(p.name)}
                  data-testid={`geo-prov-${p.name}`}
                >
                  <title>{`${p.name} — ${counts[p.name] || 0} peserta`}</title>
                </path>
              );
            })}
          </svg>
          <div className="mt-3 flex items-center justify-between text-[11px] text-white/50">
            <span data-testid="geo-hover-info">{hoverInfo || "Arahkan kursor / sentuh provinsi pada peta"}</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="inline-block w-3 h-3" style={{ background: "#18181B" }} /> 0
              <span className="inline-block w-3 h-3 ml-2" style={{ background: "#FF3B30" }} /> terbanyak
            </span>
          </div>
        </div>

        <div className="card-flat p-5">
          {sel ? (
            <div data-testid="geo-detail">
              <button onClick={() => setSelected(null)} className="text-[11px] text-white/50 inline-flex items-center gap-1 hover:text-white" data-testid="geo-back">
                <ArrowLeft size={12} /> SEMUA PROVINSI
              </button>
              <div className="font-display text-2xl mt-3">{sel.province}</div>
              <div className="font-mono-num text-[#FF3B30] text-lg">{sel.count} peserta</div>
              <div className="mt-4 space-y-2 max-h-[300px] overflow-auto pr-1">
                {sel.cities.map((c) => (
                  <div key={c.city} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                    <span className="text-white/80 flex items-center gap-1.5"><MapPin size={12} className="text-[#FF3B30] shrink-0" />{c.city}</span>
                    <span className="font-mono-num text-white/60">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="geo-top-list">
              <div className="text-[10px] tracking-widest text-white/50 font-display">TOP PROVINSI</div>
              {provinces.length === 0 ? (
                <div className="mt-4 text-sm text-white/40">Belum ada pendaftar.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  {provinces.slice(0, 8).map((p, i) => (
                    <button key={p.province} onClick={() => setSelected(p.province)} className="w-full text-left group" data-testid={`geo-top-${i}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/80 group-hover:text-white truncate">{i + 1}. {p.province}</span>
                        <span className="font-mono-num text-[#FF3B30] ml-2">{p.count}</span>
                      </div>
                      <div className="mt-1 h-1 bg-white/5">
                        <div className="h-1 bg-[#FF3B30] transition-[width] duration-500" style={{ width: `${(p.count / maxCount) * 100}%` }} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
